import {BaseRouterStoreState} from '@ngrx/router-store';
import {Data, Params, Route} from '@angular/router';

export const ROUTER_FEATURE_KEY = 'router';

export interface SerializedRouterState<RouteNameType extends string = string> extends BaseRouterStoreState {
  url: string;
  path: string;
  breadcrumbs: MxBreadcrumbs<RouteNameType>;
  fragment: string;
  params: Params;
  queryParams: Params;
  data: Data;
}

export const INITIAL_SERIALIZED_ROUTER_STATE: SerializedRouterState = {
  url: '',
  path: '',
  breadcrumbs: [],
  fragment: '',
  params: {},
  queryParams: {},
  data: {}
}

export interface MxBreadcrumb<RouteNameType extends string = string> {
  title: string;
  path: string;
  name: RouteNameType;
  params: Params;
  /** False for a route whose breadcrumb labels a group rather than a navigable page. */
  linkEnabled?: boolean;
}

export type MxBreadcrumbs<RouteNameType extends string = string> = MxBreadcrumb<RouteNameType>[];

export type PathFor<RouteNameType extends string = string> = Record<RouteNameType, string>;
export type ChildRouteNamesFor<RouteNameType extends string = string> = Record<RouteNameType, RouteNameType[]>;
export type MxRouterTabsFor<RouteNameType extends string = string> = Record<RouteNameType, MxRouterTabs<RouteNameType>>;
export type MxRouterTabIndexFor<RouteNameType extends string = string> = Partial<Record<RouteNameType, number>>;

export interface MxRouterTab<RouteNameType extends string = string> {
  index: number;
  parentRouteName: RouteNameType;
  routeName: RouteNameType;
  label: string;
}

export type MxRouterTabs<RouteNameType extends string = string> = readonly MxRouterTab<RouteNameType>[];

export type MxRouteConstants<RouteNameType extends string = string> = {
  /**
   * Given a route name, get its route path.
   *
   * @example PATH_FOR['PATRON_OVERVIEW'] // /patrons/:id/overview
   */
  readonly PATH_FOR: PathFor<RouteNameType>;

  /**
   * Given a route name, get the route names of its children, or an empty array if the route has no children.
   *
   * @example CHILD_ROUTE_NAMES_FOR['PATRON_DETAILS'] // ['PATRON_OVERVIEW', 'PATRON_NOTES', etc.]
   * @example CHILD_ROUTE_NAMES_FOR['PATRON_NOTES'] // []
   */
  readonly CHILD_ROUTE_NAMES_FOR: ChildRouteNamesFor<RouteNameType>;

  /**
   * Given a route name, get the router tab data for its children, or an empty array when the route has no children that represent tabs.
   *
   * @example ROUTER_TABS_FOR['PATRON_DETAILS'] // [{index: 0, routeName: 'PATRON_OVERVIEW', label: 'Overview'}, etc.]
   * @example ROUTER_TABS_FOR['PATRON_HOLDS'] // []
   */
  readonly ROUTER_TABS_FOR: MxRouterTabsFor<RouteNameType>;

  /**
   * Given a route name, get its router tab index, or `undefined` when the route doesn't represent a tab.
   *
   * @example ROUTER_TAB_INDEX_FOR['PATRON_ITEMS_OUT'] // 2
   * @example ROUTER_TAB_INDEX_FOR['PATRON_DETAILS'] // undefined
   */
  readonly ROUTER_TAB_INDEX_FOR: MxRouterTabIndexFor<RouteNameType>;
};

export type MxRoutes<RouteNameType extends string = string> = readonly MxRoute<RouteNameType>[];

export type MxRoute<RouteNameType extends string = string> = MxRouteLeaf<RouteNameType> | MxRouteWithChildren<RouteNameType> | MxRouteWithTabs<RouteNameType>;

type MxRouteLeaf<RouteNameType extends string = string> = Omit<Route, 'children'> & {
  name: RouteNameType;
  initialQueryParams?: Params;
};

type MxRouteWithChildren<RouteNameType extends string = string> = MxRouteLeaf<RouteNameType> & {
  children: readonly MxRoute<RouteNameType>[];
};

type MxRouteWithTabs<RouteNameType extends string = string> = MxRouteLeaf<RouteNameType> & {
  tabs: readonly MxRouteTab<RouteNameType>[];
};

export type MxRouteTab<RouteNameType extends string = string> = MxRoute<RouteNameType> & {
  tabLabel: string;
};
